<script setup>
import {computed, ref} from 'vue'
import { useBoundaryStore } from '@/stores/boundary'
import {useRouter, useRoute} from 'vue-router'
import { useWindowSize } from '@vueuse/core'
import {ChevronRight, Folder, Folders, Ellipsis, Pin, UserRoundPlus} from 'lucide-vue-next'

import {Button} from '@/components/ui/button'
import ProjectActionsMenu from './ProjectActionsMenu.vue'
import {useProjectPins} from '@/composables/useProjectPins'

const boundaryStore = useBoundaryStore()

const router = useRouter()
const route = useRoute()
const emit = defineEmits(['select', 'join'])
const props = defineProps({
  project: Object,
  openedProjectIds: Object,
  joinable: Boolean
})

const routeProjectId = computed(() => parseInt(route.params.project_id))

const isSelected = computed(() => routeProjectId.value === props.project.project_id)
const isOpened = computed(() => !!props.openedProjectIds?.[props.project.project_id])
const isHovered = ref(false)
const { width: windowWidth } = useWindowSize()
const isTouchLayout = computed(() => windowWidth.value < 1024)
const hoveredFromMap = computed(() => boundaryStore.hoveredBoundaryProjectId === props.project.project_id)

const select = (project, level = 0) => {
  emit('select', project, level)
}

const openProject = (project) => {
  if (props.joinable) {
    emit('join', project)
    return
  }
  router.push({ name: 'ProjectHome', params: { org_id: project.organization_id, project_id: project.project_id } })
}

const placeDetails = computed(() => boundaryStore.getPlaceDetails(props.project.project_id))

const {isPinned, togglePin} = useProjectPins()
const pinned = computed(() => isPinned(props.project.project_id))

const isoToEmoji = (isoCode) => {
  const codePoints = isoCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}
</script>

<template>
  <div v-if="project?.project_id" class="flex items-center px-0.5">
    <div
      class="flex items-center flex-1 w-full rounded-lg p-0.5"
      :class="{'bg-secondary': (isHovered || hoveredFromMap) && !isSelected, 'bg-primary text-white': isSelected}"
      :value="project"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    >
      <Button
        variant="ghostDark"
        class="w-7 h-7 rounded-full p-0 text-muted-foreground hover:text-black project-expand-toggle"
        :class="{'opacity-0': !project.children?.length, 'rotate-90 text-black': isOpened, 'text-white': isSelected}"
        :id="`project-expand-${project.project_id}`"
        :aria-label="isOpened ? `Collapse ${project.project_name} sub-projects` : `Expand ${project.project_name} sub-projects`"
        :aria-expanded="isOpened"
        :aria-controls="`project-children-${project.project_id}`"
        :title="isOpened ? `Collapse ${project.project_name} sub-projects` : `Expand ${project.project_name} sub-projects`"
        type="button"
        :disabled="!project.children?.length"
        @click.stop
        @click="select(project)"
      >
        <ChevronRight class="w-7 h-5" :aria-hidden="true" />
      </Button>
      <div class="flex items-center justify-between w-full min-w-[200px]">
        <Button
          variant="ghost"
          class="flex items-center h-full p-0.5 cursor-pointer min-w-0 flex-1 justify-start hover:bg-transparent"
          :class="{'truncate': isHovered}"
          :id="`project-name-${project.project_id}`"
          :aria-label="joinable ? `Join ${project.project_name}` : `Open ${project.project_name} project`"
          :title="joinable ? `Join ${project.project_name}` : `Open ${project.project_name} project`"
          type="button"
          @click.stop="openProject(project)"
        >
          <div class="w-5 h-5 mr-2 flex items-center justify-center">
            <span v-if="project.emoji || placeDetails" class="text-lg leading-none">
              {{ project.emoji || (placeDetails ? isoToEmoji(placeDetails.country_code) : '') }}
            </span>
            <Folder v-if="!project.children?.length && !project.emoji" class="w-4.5 h-4.5 text-foreground" :class="{'text-white': isSelected}" />
            <Folders v-else-if="!placeDetails && !project.emoji" class="w-4.5 h-4.5 text-foreground" :class="{'text-white': isSelected}" />
          </div>
          <span class="truncate" :title="project.project_name">
            {{ project.project_name }}
          </span>
        </Button>

        <div v-if="joinable" class="flex items-center justify-end project-actions" @click.stop>
          <Button
            variant="secondary"
            size="sm"
            class="h-6 px-2 text-xs"
            :class="{'opacity-0': !isTouchLayout && !isHovered}"
            :aria-label="`Join ${project.project_name}`"
            type="button"
            @click="emit('join', project)"
          >
            <UserRoundPlus class="w-3.5 h-3.5 mr-1" />
            Join
          </Button>
        </div>
        <div v-else class="flex items-center justify-end project-actions" @click.stop>
          <Button
            variant="ghostDark"
            class="w-7 h-7 rounded-full p-0 hover:text-black"
            :class="[
              pinned ? 'text-primary' : 'text-muted-foreground',
              isSelected ? 'text-white' : '',
              !isTouchLayout && !isHovered && !isSelected && !pinned ? 'opacity-0' : ''
            ]"
            :aria-label="pinned ? `Unpin ${project.project_name}` : `Pin ${project.project_name}`"
            :aria-pressed="pinned"
            :title="pinned ? 'Unpin project' : 'Pin project'"
            type="button"
            @click="togglePin(project.project_id)"
          >
            <Pin class="w-4 h-4" :class="pinned ? 'fill-current' : ''" />
          </Button>
          <div :class="{'opacity-0': !isTouchLayout && !isHovered && !isSelected}">
            <ProjectActionsMenu :project="project">
              <template #trigger>
                <Button
                  variant="ghostDark"
                  class="w-7 h-7 rounded-full p-0 text-muted-foreground hover:text-black"
                  :class="{'text-white': isSelected}"
                  type="button"
                >
                  <Ellipsis class="w-4 h-4" />
                </Button>
              </template>
            </ProjectActionsMenu>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    class="pl-4 pr-1"
    v-if="isOpened && !joinable"
    :id="`project-children-${project.project_id}`"
    role="group"
    :aria-label="`Sub-projects of ${project.project_name}`"
  >
    <ProjectListItem
      v-for="child in project.children"
      :key="child.project_id"
      :project="child"
      :opened-project-ids="openedProjectIds"
      @select="(project, level) => select(project, level + 1)"
    />
  </div>
</template>
